import { Badge, Box, Group, Loader, Text, Tooltip } from '@mantine/core';
import { BarChart2, Star } from 'react-feather';
import { ContentCard } from '../../../../components/common/ContentCard';
import type { AirtimeEnvelope } from '../../../../services/api/coachPostSession';

/**
 * MED-894 (B7) — the "Airtime Balance" card: the aggregate leader-vs-participants
 * speaking-time split, computed server-side from the persisted diarized
 * transcript (the shared airtime foundation) and consumed here as a resolved
 * envelope — never computed client-side.
 *
 * Deliberately VISUAL ONLY (AC6): the split bar and nothing else. No
 * interpretation/summary sentence (that's B10's "In-session insights" card) and
 * no compare-to-last-session chip (excluded 7/10 — the client concept shows
 * one; its omission here is intentional, not a miss). The per-participant bars
 * (B6 / MED-1192) will render inside this same card on the coach view later.
 *
 * Role-appropriate labels (AC5): the coach view reads "Coach (You)" /
 * "Participants"; the session-leader view reads "Session Leader (You)" /
 * "Peers". Same numbers, different framing.
 *
 * Visibility (AC7): AI-only surface — absent entirely when AI wasn't active
 * for the session (`aiAvailable`, MED-1128 gate), deferring the single AI-off
 * explanation to the Recap Email Draft card (the shared post-session AI-off
 * pattern). Within that: pending → loading line; not_available → honest "not
 * available" line (never a fabricated or 0%/0% chart); available → the split bar.
 *
 * MED-1207 (D16): coach-only. The coach post-session page renders this card;
 * the peer-leader page no longer does. `viewerRole` is retained (B7 left as-is
 * per the ticket), so the `peer_leader` label branch is currently unused.
 *
 * Colors are the platform's brand green scale (the mock's orange/green was
 * illustrative): leader = brand dark green, participants = light brand green —
 * one hue family at two weights, reading as parts of one whole.
 */

const BAND_DIVIDER = '#D1D5DB';
const VALUE_COLOR = '#374151';
const LABEL_COLOR = '#6B7280';
const LEADER_FILL = '#28463A'; // brand primary (dark green)
const LEADER_TEXT = '#FFFFFF';
const PARTICIPANTS_FILL = '#c5d6c8'; // brand green, light
const PARTICIPANTS_TEXT = '#28463A';

/** Below this share a segment is too narrow to hold its own labels. */
const MIN_LABEL_PCT = 12;

/**
 * MED-1275 (B6 UAT) — the product threshold for the per-participant bars.
 * 1–12 participants keep every percentage on screen (fit-to-width); 13+ falls
 * back to hover/focus. 12 is the largest group size actually in use, so the
 * fallback is unreachable for every group being run today (AC10).
 */
const PERCENT_VISIBLE_MAX = 12;

/** Fixed height of the per-participant bar track; bars grow up from the baseline. */
const PARTICIPANT_BAR_TRACK_PX = 72;

/**
 * MED-1275 AC3 — the percentage type size scales with the participant count:
 * comfortable for typical 6–8 groups, tightening only as the count approaches
 * the 12 ceiling. Not a single small size fixed at whatever survives 12.
 */
function percentFontSize(count: number): number {
  if (count <= 6) return 12;
  if (count <= 8) return 11;
  if (count <= 10) return 10;
  return 9; // 11–12
}

/** Column gap tightens with the count so 12 columns still fit the narrow card (AC1). */
function participantColumnGap(count: number): number {
  if (count <= 6) return 10;
  if (count <= 9) return 8;
  if (count <= PERCENT_VISIBLE_MAX) return 6;
  return 4; // above 12 — pack tighter still, never scroll (AC7)
}

interface Props {
  /** The bundle's airtime envelope. Null = AI wasn't active (card absent). */
  airtime: AirtimeEnvelope | null;
  /** MED-1128 effective gate — modality AI && session listening. */
  aiAvailable: boolean;
  /** True while the post-session bundle is genuinely in flight. */
  loading: boolean;
  /** Which view this card renders in — drives the bucket labels (AC5). */
  viewerRole: 'coach' | 'peer_leader';
}

export function AirtimeBalanceCard({ airtime, aiAvailable, loading, viewerRole }: Props) {
  // AI-only surface: absent entirely when AI wasn't active for the session
  // (the Recap Email Draft card carries the single AI-off explanation).
  // Loose == also catches `undefined` — a bundle from an API deployed before
  // this field exists must render "no card", never crash the page.
  if (!aiAvailable || airtime == null) return null;

  const split = airtime.status === 'available' ? airtime.split : undefined;

  // MED-1192 (B6) — present only on the coach bundle. Bars scale relative to
  // the largest participant share so the tallest bar is always full height.
  const perParticipant = (airtime.status === 'available' ? airtime.per_participant : undefined) ?? [];
  const maxParticipantPct = Math.max(1, ...perParticipant.map(p => p.share_of_participants_pct));

  // MED-1275 AC15/AC16 — render largest share first (replacing MED-1192's
  // roster order on this surface only). Copy before sorting so the payload
  // isn't mutated; the label tiebreak (numeric-aware) makes the order stable
  // across re-renders of the same session.
  const sortedParticipants = [...perParticipant].sort(
    (a, b) =>
      b.share_of_participants_pct - a.share_of_participants_pct ||
      a.label.localeCompare(b.label, undefined, { numeric: true }),
  );

  // MED-1275 AC2/AC3/AC8/AC10 — 1–12 keep every percentage on screen; 13+
  // suppresses the always-on percentages and moves the value to hover/focus.
  const participantCount = sortedParticipants.length;
  const showParticipantPercentages = participantCount <= PERCENT_VISIBLE_MAX;
  const participantPercentFz = percentFontSize(participantCount);
  const participantGap = participantColumnGap(participantCount);

  // MED-1275 AC13/AC14 — one accessible description of the whole distribution,
  // participant-by-participant, using the anonymized labels still on the
  // payload. Mirrors the aggregate split bar's role="img" + aria-label and, by
  // enumerating every participant, covers all group sizes including the >12
  // fallback where the visible percentages are gone (AC9).
  const participantDistributionLabel =
    participantCount > 0
      ? `Individual participant airtime, largest share first: ${sortedParticipants
          .map(p => `${p.label} ${p.share_of_participants_pct}%`)
          .join(', ')}.`
      : '';

  // Labels derive from WHO the leader bucket is (leader_kind), not just the
  // page: a coach reviewing a PEER-LED session sees "Session Leader" (without
  // "(You)" — the bucket isn't them), never a mislabeled "Coach (You)". The
  // "(You)" suffix applies only when the viewer IS the leader bucket: the
  // coach on a coach-led session, or the session leader on their own page
  // (that page is leader-only via assertPeerLeader).
  const leaderIsViewer =
    (viewerRole === 'coach' && split?.leader_kind === 'coach') ||
    (viewerRole === 'peer_leader' && split?.leader_kind === 'peer_leader');
  const leaderBase = split?.leader_kind === 'peer_leader' ? 'Session Leader' : 'Coach';
  const leaderLabel = leaderIsViewer ? `${leaderBase} (You)` : leaderBase;
  const participantsLabel = viewerRole === 'coach' ? 'Participants' : 'Peers';
  const pending = loading || airtime.status === 'pending';

  return (
    <ContentCard
      variant="elevated"
      padding={0}
      contentGap={0}
      fill={false}
      header={null}
      data-testid="airtime-balance-card"
      style={{ overflow: 'hidden' }}
    >
      {/* Header band — mirrors the sibling post-session AI cards. */}
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        style={{ padding: '16px 20px', borderBottom: `1px solid ${BAND_DIVIDER}` }}
      >
        <Group gap={8} align="center" wrap="nowrap">
          <BarChart2 size={15} color={VALUE_COLOR} />
          <Text fw={700} fz={14} c={VALUE_COLOR}>
            Airtime Balance
          </Text>
        </Group>
        {split && (
          <Badge
            size="sm"
            variant="light"
            leftSection={<Star size={10} />}
            styles={{
              root: {
                backgroundColor: '#f3f0ff',
                color: '#5a3eb6',
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            AI-generated
          </Badge>
        )}
      </Group>

      <Box style={{ padding: 20 }}>
        {pending ? (
          <Group gap={8} align="center">
            <Loader size="xs" />
            <Text size="sm" c={LABEL_COLOR}>
              Analyzing this session&apos;s airtime…
            </Text>
          </Group>
        ) : !split ? (
          // Honest "not available" — never a fabricated or 0%/0% chart (AC7).
          // MED-1212: the retention-off case no longer exists (retention is always
          // on; the card is simply absent when AI wasn't active). What remains is
          // the honest "AI ran but there's no usable airtime" line.
          <Text size="sm" c={LABEL_COLOR} data-testid="airtime-not-available">
            Airtime isn&apos;t available for this session.
          </Text>
        ) : (
          <>
          <Group
            gap={0}
            wrap="nowrap"
            style={{ borderRadius: 8, overflow: 'hidden' }}
            data-testid="airtime-split-bar"
            role="img"
            aria-label={`${leaderLabel} ${split.leader_share_pct}%, ${participantsLabel} ${split.participants_share_pct}%`}
          >
            {/* A 0% segment renders nothing at all — even fixed padding would
                paint a phantom stripe the numbers say doesn't exist (AC7). */}
            {split.leader_share_pct > 0 && (
              <Box
                style={{
                  width: `${split.leader_share_pct}%`,
                  minWidth: 8,
                  backgroundColor: LEADER_FILL,
                  padding: '10px 12px',
                }}
              >
                {split.leader_share_pct >= MIN_LABEL_PCT && (
                  <>
                    <Text fw={700} fz={14} c={LEADER_TEXT}>
                      {split.leader_share_pct}%
                    </Text>
                    <Text fz={11} c={LEADER_TEXT} style={{ opacity: 0.85, whiteSpace: 'nowrap' }}>
                      {leaderLabel}
                    </Text>
                  </>
                )}
              </Box>
            )}
            {split.participants_share_pct > 0 && (
              <Box
                style={{
                  width: `${split.participants_share_pct}%`,
                  minWidth: 8,
                  backgroundColor: PARTICIPANTS_FILL,
                  padding: '10px 12px',
                  textAlign: 'right',
                }}
              >
                {split.participants_share_pct >= MIN_LABEL_PCT && (
                  <>
                    <Text fw={700} fz={14} c={PARTICIPANTS_TEXT}>
                      {split.participants_share_pct}%
                    </Text>
                    <Text
                      fz={11}
                      c={PARTICIPANTS_TEXT}
                      style={{ opacity: 0.85, whiteSpace: 'nowrap' }}
                    >
                      {participantsLabel}
                    </Text>
                  </>
                )}
              </Box>
            )}
          </Group>

          {/* MED-1192 (B6) / MED-1275 (B6 UAT) — anonymized per-participant
              bars, COACH VIEW ONLY (AC5/AC20). Scoped server-side (only the
              coach bundle carries per_participant) AND client-side (viewerRole
              guard) so a future server regression can't render bars on the
              leader view — the house rule that server and client gates agree.
              Shares of the PARTICIPANT bucket (MED-1192 AC4), now sorted
              descending (AC15/16) and fit to the card width instead of the old
              horizontally scrolling fixed-width strip.

              Layout (AC1/AC4/AC6/AC7): equal-width flex columns (flex:1 1 0,
              minWidth:0) inside a wrap="nowrap" group with NO overflow — every
              bar shares the width and fits at once at both the narrow desktop
              right column and the full-width mobile layout, at any group size.
              Each column is a fixed-height bar track so the percentages line up
              in one row at the top and bars grow from a common baseline;
              relative heights still reflect share and the Math.max floor keeps
              a 1% participant visible (AC5). */}
          {viewerRole === 'coach' && participantCount > 0 && (
            <Box mt={16} data-testid="airtime-per-participant-bars">
              {/* AC11/AC12 — per-bar `Participant N` labels removed; one caption
                  identifies the row (continuation of the "Participants" bucket). */}
              <Text fz={12} fw={600} c={LABEL_COLOR} mb={8}>
                Individual participants
              </Text>
              <Group
                gap={participantGap}
                wrap="nowrap"
                align="flex-end"
                role="img"
                aria-label={participantDistributionLabel}
              >
                {sortedParticipants.map(p => {
                  const barHeight = Math.max(
                    6,
                    (p.share_of_participants_pct / maxParticipantPct) * PARTICIPANT_BAR_TRACK_PX,
                  );
                  const column = (
                    <Box
                      key={p.label}
                      style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}
                      // AC8/AC9 — above 12 the always-on percentage is gone, so
                      // each bar becomes keyboard-focusable and the Tooltip
                      // (hover + focus) exposes the exact value without a pointer.
                      tabIndex={showParticipantPercentages ? undefined : 0}
                    >
                      {showParticipantPercentages && (
                        <Text
                          fw={600}
                          fz={participantPercentFz}
                          c={VALUE_COLOR}
                          style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}
                        >
                          {p.share_of_participants_pct}%
                        </Text>
                      )}
                      <Box
                        style={{
                          height: PARTICIPANT_BAR_TRACK_PX,
                          display: 'flex',
                          alignItems: 'flex-end',
                          marginTop: showParticipantPercentages ? 4 : 0,
                        }}
                      >
                        <Box
                          style={{
                            width: '100%',
                            height: barHeight,
                            backgroundColor: PARTICIPANTS_FILL,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                  return showParticipantPercentages ? (
                    column
                  ) : (
                    <Tooltip
                      key={p.label}
                      label={`${p.label}: ${p.share_of_participants_pct}%`}
                      withArrow
                      events={{ hover: true, focus: true, touch: false }}
                    >
                      {column}
                    </Tooltip>
                  );
                })}
              </Group>
            </Box>
          )}
          </>
        )}
      </Box>
    </ContentCard>
  );
}

export default AirtimeBalanceCard;
